// Builds and mounts the promo app. Two callers: main.js (standalone, `#app` in promo's own
// document) and webshell-main.js (embedded in the One Retail shell, mounted into a host element the
// shell owns). See rtls-one-retail/frontend/shell/docs/integrations/promo.md.

// importing filters
import * as dateFilters from './src/js/filters/date-filters';
import * as generalFilters from './src/js/filters/general-filters';

import { get } from 'lodash';
import { createApp, configureCompat } from 'vue';
import { createPromoVuetify, pathToComponentName, setWeekNumberResolver } from './src/js/vuetify';

import App from '@/App.vue';
import dateUtilsFactory from '../shared-modules/date-utils-factory';
// importing plugins
import i18n from './src/js/vue-i18n';
import globalMixins from './src/js/mixins';
import readOnlyMixin from './src/js/mixins/readonly';
import eventsMixin from './src/js/mixins/events';
import Axios from './src/js/axios';
import promoUtilsPlugin from '/src/js/plugins/promo-utils';
import store from './src/js/store';
import mitt from 'mitt';
import vueScrollto from 'vue-scrollto';
import { ObserveVisibility } from 'vue3-observe-visibility';
import { ModuleRegistry, AllCommunityModule, provideGlobalGridOptions } from 'ag-grid-community';
import { LicenseManager, AllEnterpriseModule } from 'ag-grid-enterprise';
import draggable from 'vuedraggable';

import HighchartsVue from 'highcharts-vue';
import 'highcharts/highcharts-more';

// routing imports
import createAppRouter from './src/js/routes/index';
import { MAINTENANCE_ROUTE_PATH } from './src/js/routes/maintenance-guard';
import { isEmbedded } from './src/js/webshell-embed/embedded';
import installShellContributions from './src/js/webshell-embed/shell-contributions';
// AG-grid imports
import 'ag-grid-enterprise';
// ToDo: import from package
import './style/rtls-ui-components.css';
// Import Local Styles
import '@style/main.scss';
// Import numeralJS locales
import '@/lang/numeral';

// import all components excluding icons
const vueComponents = import.meta.glob(
    ['./src/js/**/*.vue', '!./src/js/components/component-icons/**/*.vue'],
    { eager: true }
);

configureCompat({
    MODE: 3,
});

// Mark all grids as using legacy themes and disable the ag-grid context menu globally.
provideGlobalGridOptions({ theme: 'legacy', suppressContextMenu: true });

ModuleRegistry.registerModules([AllCommunityModule, AllEnterpriseModule]);

// Set ag-grid license
LicenseManager.setLicenseKey(
    'Using_this_{AG_Grid}_Enterprise_key_{AG-054150}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Wyman_Oliver_Inc}_is_granted_a_{Multiple_Applications}_Developer_License_for_{6}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_{14}_Production_Environments___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{24_February_2025}____[v3]_[01]_MTc0MDM1NTIwMDAwMA==e41778debb5bf8aae8a4f23395613f6c'
);

/**
 * Builds an ISO date at UTC midnight using local date parts to avoid timezone drift.
 *
 * @param {Date} date - Date from Vuetify adapter internals.
 * @returns {string} UTC date string in ISO format.
 */
const toUtcMidnightIso = date => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00:00.000Z`;
};

/**
 * Configures the global week-number resolver used by the Vuetify date adapter.
 */
const configureWeekNumberResolver = () => {
    setWeekNumberResolver(date => {
        if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
            return null;
        }

        const getWeekByDate = store.getters['weeks/getWeekByDate'];
        if (typeof getWeekByDate !== 'function' || !store.state.moment) {
            return null;
        }

        const week = getWeekByDate(toUtcMidnightIso(date));
        const weekOfYear = Number(week?.weekOfYear);
        return Number.isInteger(weekOfYear) ? weekOfYear : null;
    });
};

// `routerBase` is the shell prefix promo is served under embedded (e.g. `/acme/promo`);
// `initialPath` is the promo-internal path the shell opened.
//
// Whether promo is embedded is read from the `embedded` module, not passed in: the entry point
// settles it before calling, along with the rest of the embedded-only setup (api origin, CSS scope
// class, tenant seed) that has to be in place before the first request goes out.
export default async function createPromoApp({
    mountTarget = '#app',
    routerBase = import.meta.env.BASE_URL,
    initialPath = '/',
} = {}) {
    const embedded = isEmbedded();

    const el = typeof mountTarget === 'string' ? document.querySelector(mountTarget) : mountTarget;
    const router = createAppRouter(routerBase, { embedded });
    const vuetify = createPromoVuetify({ embedded });

    // App uses <script setup>; its setup() would be dropped if wrapped via extends/mixins.
    // globalMixins are already applied globally below via app.mixin(), so no root-level merge is
    // needed.
    const app = createApp(App);
    let mounted = false;

    const emitter = mitt();

    app.config.globalProperties.emitter = emitter;
    store.emitter = emitter;

    app.use(i18n);
    app.mixin(globalMixins[0]);
    app.mixin(eventsMixin);
    app.mixin(readOnlyMixin);
    app.use(store);
    app.use(Axios, { router, store, i18n });
    app.use(vuetify);
    app.use(vueScrollto);
    app.use(HighchartsVue);

    app.directive('observe-visibility', ObserveVisibility);

    app.component('draggable', draggable);

    // register all components inside app
    Object.entries(vueComponents).forEach(([path, definition]) => {
        const componentName = pathToComponentName(path);
        app.component(componentName, definition.default);
    });

    await Promise.resolve(store.dispatch('context/loadContext'))
        .then(async () => {
            if (store.state.context.isMaintenanceModeEnabled) {
                app.use(router);
                await router.replace({ path: MAINTENANCE_ROUTE_PATH });
                if (!el.isConnected) return app;
                app.mount(el);
                mounted = true;
                return app;
            }

            if (!embedded) {
                await store.dispatch('context/initialiseTenantFromQuery');
            }
            const mountApp = async () => {
                // Authenticated startup installs Moment via loadUserContext action, before loading week data.
                // If unauthenticated, initialise moment here using the public Vue configuration (loadVueConfig)
                const momentInstance =
                    store.state.moment || (await store.dispatch('initialiseMoment'));
                app.config.globalProperties.$moment = momentInstance;
                store.moment = momentInstance;
                configureWeekNumberResolver();
                app.use(promoUtilsPlugin, {
                    utils: {
                        dateUtils: dateUtilsFactory(momentInstance),
                    },
                });
                // Apply filters like a global functions
                Object.entries(dateFilters).forEach(([key, value]) => {
                    app.config.globalProperties[key] = value.bind(null, momentInstance);
                });
                Object.entries(generalFilters).forEach(([key, value]) => {
                    app.config.globalProperties[key] = value;
                });

                // Before the first navigation, in both modes: the weekly metrics fetched here cover
                // every campaign, and a deep link's guards would otherwise select one first.
                if (!get(store, 'state.clientConfig.toggleLogic.reportingOnly', false)) {
                    store.dispatch('initialiseStateBackgroundPostAppCreation', null, {
                        root: true,
                    });
                }

                // A memory history starts at '/', so seed the path the shell opened us on. After the
                // moment setup, which route guards use, and before `app.use(router)`, which navigates
                // only from START_LOCATION.
                if (embedded) {
                    await router
                        .replace(initialPath || '/')
                        .catch(error =>
                            console.error('[promo] failed to open', initialPath, error)
                        );
                }
                app.use(router);

                // If user has navigated away by the time we get here, or the webshell has aborted the mount
                // in some way, the `el` won't be connected to the HTML page anymore.
                // In that's the case, do not mount the Vue app.
                if (!el.isConnected) return app;

                // Start the Vue application
                app.mount(el);
                mounted = true;
                // AG-grid Register all Community and Enterprise features
                ModuleRegistry.registerModules([AllEnterpriseModule]);

                // Everything promo publishes to the shell, and current locale from the shell
                if (embedded) installShellContributions(router);

                return app;
            };

            // Standalone, a failed load still mounts so the router guard sends the user to login;
            // embedded, the shell owns login, so the failure rejects the mount.
            return Promise.resolve(store.dispatch('context/loadUserContext')).then(
                mountApp,
                async error => {
                    if (!embedded) await mountApp();
                    throw error;
                }
            );
        })
        // Embedded, the shell shows its load error for a rejected mount; standalone, log it.
        .catch(error => {
            if (embedded) throw error;
            console.error('[promo] bootstrap failed', error);
        });

    // Null when there was no host `el` left to mount into.
    return mounted ? app : null;
}
